export const CURRENT_SCHEMA_VERSION = 4;

export type MigrationFn = (state: any) => any;

/**
 * Registry of migration functions.
 * The key represents the target version to migrate TO.
 * e.g., key `2` is a migration from version 1 to 2.
 */
export const migrations: Record<number, MigrationFn> = {
  2: (state: any) => {
    // Migration from v1 to v2:
    // 1. Convert trafficRoutes from category-based object to flat array format.
    const oldRoutes = state.trafficRoutes;
    const newRoutes: any[] = [];

    if (oldRoutes && !Array.isArray(oldRoutes)) {
      const categories = [
        { name: "staticRead", type: "static", readRatio: 100 },
        { name: "dynamicRead", type: "dynamic", readRatio: 100 },
        { name: "dynamicWrite", type: "dynamic", readRatio: 0 },
      ];

      categories.forEach(({ name, type, readRatio }) => {
        const routesList = oldRoutes[name];
        if (Array.isArray(routesList)) {
          routesList.forEach((r: any) => {
            const v2RouteId = `${r.id}_${name}`;
            newRoutes.push({
              id: v2RouteId,
              path: r.path,
              weight: r.weight ?? 100,
              contentTypes: [
                {
                  id: `${v2RouteId}-ct`,
                  type: type,
                  weight: 100,
                  readRatio: readRatio,
                },
              ],
            });
          });
        }
      });
    }

    const migratedState = {
      ...state,
      trafficRoutes: newRoutes.length > 0 ? newRoutes : state.trafficRoutes,
    };

    // 2. Convert routeAssignments on load balancer blocks
    if (migratedState.blocks && typeof migratedState.blocks === "object") {
      const updatedBlocks = { ...migratedState.blocks };
      let blocksModified = false;

      Object.keys(updatedBlocks).forEach((blockId) => {
        const block = updatedBlocks[blockId];
        if (
          block &&
          block.dynamicProperties &&
          block.dynamicProperties.routeAssignments
        ) {
          const oldAssignments = block.dynamicProperties.routeAssignments;
          // Check if assignments are in the old v1 format (nested objects per category)
          const hasV1Format = ["staticRead", "dynamicRead", "dynamicWrite"].some(
            (category) =>
              oldAssignments[category] && typeof oldAssignments[category] === "object"
          );

          if (hasV1Format) {
            const v2Assignments: Record<string, string> = {};
            const mapping = [
              { name: "staticRead", type: "static", op: "read" },
              { name: "dynamicRead", type: "dynamic", op: "read" },
              { name: "dynamicWrite", type: "dynamic", op: "write" },
            ];

            mapping.forEach(({ name, type, op }) => {
              const categoryMap = oldAssignments[name];
              if (categoryMap && typeof categoryMap === "object") {
                Object.entries(categoryMap).forEach(([rId, targetBlockId]) => {
                  const v2RouteId = `${rId}_${name}`;
                  const assignmentKey = `${v2RouteId}_${type}_${op}`;
                  if (targetBlockId) {
                    v2Assignments[assignmentKey] = targetBlockId as string;
                  }
                });
              }
            });

            updatedBlocks[blockId] = {
              ...block,
              dynamicProperties: {
                ...block.dynamicProperties,
                routeAssignments: v2Assignments,
              },
            };
            blocksModified = true;
          }
        }
      });

      if (blocksModified) {
        migratedState.blocks = updatedBlocks;
      }
    }

    return migratedState;
  },
  3: (state: any) => {
    // Migration from v2 to v3:
    // Add minTPS and maxTPS fields to GameState if not already present.
    const initialRootTotal = state.initialRootIOPS?.total ?? 20;
    return {
      ...state,
      minTPS: state.minTPS !== undefined ? state.minTPS : 10,
      maxTPS: state.maxTPS !== undefined ? state.maxTPS : Math.max(initialRootTotal, 100),
    };
  },
  4: (state: any) => {
    // Migration from v3 to v4:
    // Add activeProblemId (interview problem library) if not already present.
    return {
      ...state,
      activeProblemId: state.activeProblemId !== undefined ? state.activeProblemId : null,
    };
  },
};

/**
 * Runs migrations sequentially from a starting version to a target version.
 */
export const runMigrations = (state: any, fromVersion: number, toVersion: number): any => {
  let migratedState = state;
  for (let v = fromVersion + 1; v <= toVersion; v++) {
    const migration = migrations[v];
    if (migration) {
      migratedState = migration(migratedState);
    }
  }
  return migratedState;
};

/**
 * Restores a design from stored data.
 * - Handles new wrapped states: { version: number, state: GameState }
 * - Defaults any unversioned stored state to v1.
 * - Safely unwraps { game: GameState } legacy wrappers.
 * - Performs migrations if the stored version does not match CURRENT_SCHEMA_VERSION.
 */
export const restoreDesign = (storedData: any): any => {
  if (!storedData) return undefined;

  let parsedData = storedData;
  if (typeof storedData === "string") {
    try {
      parsedData = JSON.parse(storedData);
    } catch {
      // not a JSON string, fallback to original
    }
  }

  let version = 1;
  let state = parsedData;

  // 1. Detect if it's a version-wrapped design: { version: number, state: GameState }
  if (
    parsedData &&
    typeof parsedData === "object" &&
    "version" in parsedData &&
    "state" in parsedData
  ) {
    version = parsedData.version ?? 1;
    state = parsedData.state;
  } else {
    // 2. Unversioned legacy states are assumed to be v1.
    // If it's a legacy state containing the root-level "game" object, unwrap it.
    if (parsedData && typeof parsedData === "object" && "game" in parsedData) {
      state = parsedData.game;
    }
  }

  // 3. Migrate if versions do not match the current schema version
  if (version !== CURRENT_SCHEMA_VERSION) {
    state = runMigrations(state, version, CURRENT_SCHEMA_VERSION);
  }

  return state;
};
